import { LoadingButton } from "@mui/lab";
import { FieldId } from "Backend";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";
import { schemas, useForm } from "helpers/FormHelpers";
import { FormTextField } from "helpers/FormHelpersComponents";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "redux/hooks";
import {
  selectLensEditorFieldTagConfigById,
  selectLensEditorTags,
  upsertFieldTagConfig,
} from "redux/slices/lensEditor";
import TagSelect from "components/tags/TagSelect";
import { STARRED_ENTRY, STARRED_TAG, UNTAGGED, UNTAGGED_ENTRY } from "components/tags/constants";

function EntailmentThresholdHelperText() {
  return (
    <div className="divide-y divide-gray-100 *:py-2 first:*:pt-0 last:*:pb-0">
      <p className="text-xs italic">
        Entailment Threshold is the threshold below which the system will create Low Confidence tags when running
        autoreview. Should be between 0 and 1.
      </p>
      <p className="text-xs italic">Default: 0.1</p>
    </div>
  );
}

function AllowedTagsHelperText() {
  return (
    <div className="divide-y divide-gray-100 *:py-2 first:*:pt-0 last:*:pb-0">
      <p className="text-xs italic">Allowed Tags is the list of tags that should be appear in profile.</p>
    </div>
  );
}

function ReadOnlyTagsHelperText() {
  return (
    <div className="divide-y divide-gray-100 *:py-2 first:*:pt-0 last:*:pb-0">
      <p className="text-xs italic">
        Read-Only Tags are not be editable in the UI. They can be set by scripts or the backend.
      </p>
      <p className="text-xs italic">Tags here must also appear in Allowed Tags.</p>
    </div>
  );
}

function SupportOnlyTagsHelperText() {
  return (
    <div className="divide-y divide-gray-100 *:py-2 first:*:pt-0 last:*:pb-0">
      <p className="text-xs italic">Support-Only Tags only appear for support users.</p>
      <p className="text-xs italic">Tags here must also appear in Allowed Tags.</p>
    </div>
  );
}

function FilteredByDefaultTagsHelperText() {
  return (
    <div className="divide-y divide-gray-100 *:py-2 first:*:pt-0 last:*:pb-0">
      <p className="text-xs italic">These tags are filtered by default wherever the entry table appears.</p>
      <p className="text-xs italic">Tags here must also appear in Allowed Tags, and should not be Support Only Tags.</p>
    </div>
  );
}

export type TagConfigFormFields = "allowed" | "readOnly" | "supportOnly" | "filteredByDefault" | "entailmentThreshold";
interface FieldTagConfigFormProps {
  fieldId: FieldId;
  hasAiConfig: boolean;
}

export function FieldTagConfigForm({ fieldId, hasAiConfig }: FieldTagConfigFormProps) {
  const [pending, setPending] = useState(false);
  const snackpatch = useAppDispatchWithSnackbar();
  const tagConfig = useAppSelector((state) => selectLensEditorFieldTagConfigById(state, fieldId));
  const tags = useAppSelector(selectLensEditorTags);
  const [tagMap, withFakeTags] = useMemo(() => {
    const tagMap = new Map(tags.map((t) => [t.systemName, t]));
    const withFakeTags = new Map(tags.map((t) => [t.systemName, t]));
    withFakeTags.set(STARRED_TAG, STARRED_ENTRY);
    withFakeTags.set(UNTAGGED, UNTAGGED_ENTRY);
    return [tagMap, withFakeTags];
  }, [tags]);
  const tagConfigForm = useForm<TagConfigFormFields>(
    {
      isValid: false,
      values: {
        allowed: tagConfig?.allowed || [],
        readOnly: tagConfig?.readOnly || [],
        supportOnly: tagConfig?.supportOnly || [],
        filteredByDefault: tagConfig?.filteredByDefault || [],
        entailmentThreshold: tagConfig?.entailmentThreshold || "",
      },
      touched: {
        allowed: false,
        readOnly: false,
        supportOnly: false,
        filteredByDefault: false,
        entailmentThreshold: false,
      },
      errors: {
        allowed: [],
        readOnly: [],
        supportOnly: [],
        filteredByDefault: [],
        entailmentThreshold: [],
      },
    },
    {
      allowed: schemas.optionalArray,
      readOnly: schemas.optionalArray,
      supportOnly: schemas.optionalArray,
      filteredByDefault: schemas.optionalArray,
      entailmentThreshold: schemas.customType,
    }
  );
  const formState = tagConfigForm.formStateHandle[0];

  const save = () => {
    setPending(true);
    snackpatch(
      upsertFieldTagConfig({
        fieldId,
        allowed: formState.values.allowed,
        readOnly: formState.values.readOnly,
        supportOnly: formState.values.supportOnly,
        filteredByDefault: formState.values.filteredByDefault,
        entailmentThreshold: parseFloat(formState.values.entailmentThreshold),
      }),
      {
        successMessage: "Tag config updated",
        errorMessage: "Error updating Tag config",
      }
    ).finally(() => setPending(false));
  };

  useEffect(() => {
    tagConfigForm.updateField("allowed")(tagConfig?.allowed || []);
    tagConfigForm.updateField("readOnly")(tagConfig?.readOnly || []);
    tagConfigForm.updateField("supportOnly")(tagConfig?.supportOnly || []);
    tagConfigForm.updateField("filteredByDefault")(tagConfig?.filteredByDefault || []);
    tagConfigForm.updateField("entailmentThreshold")(tagConfig?.entailmentThreshold);
  }, [tagConfig]);

  return (
    <div className="grid gap-4 items-baseline grid-cols-2">
      {hasAiConfig && (
        <div className="flex items-center gap-2">
          <FormTextField
            className="grow"
            id="entailmentThreshold"
            label="Entailment Threshold"
            useForm={tagConfigForm}
          />
          <DescriptionTooltip description={<EntailmentThresholdHelperText />} />
        </div>
      )}
      <div className="flex items-center gap-2">
        <TagSelect
          label="Allowed Tags"
          tagMap={tagMap}
          value={formState.values?.allowed || []}
          onChange={tagConfigForm.updateField("allowed")}
        />
        <DescriptionTooltip description={<AllowedTagsHelperText />} />
      </div>
      <div className="flex items-center gap-2">
        <TagSelect
          label="Read-Only Tags"
          tagMap={tagMap}
          value={formState.values?.readOnly || []}
          onChange={tagConfigForm.updateField("readOnly")}
        />
        <DescriptionTooltip description={<ReadOnlyTagsHelperText />} />
      </div>
      <div className="flex items-center gap-2">
        <TagSelect
          label="Support-Only Tags"
          tagMap={tagMap}
          value={formState.values?.supportOnly || []}
          onChange={tagConfigForm.updateField("supportOnly")}
        />
        <DescriptionTooltip description={<SupportOnlyTagsHelperText />} />
      </div>
      <div className="flex items-center gap-2">
        <TagSelect
          label="Filtered By Default"
          tagMap={withFakeTags}
          value={formState.values?.filteredByDefault || []}
          onChange={tagConfigForm.updateField("filteredByDefault")}
        />
        <DescriptionTooltip description={<FilteredByDefaultTagsHelperText />} />
      </div>
      <div className="flex col-span-2 justify-end">
        <LoadingButton variant="contained" loading={pending} disabled={!formState.isValid} onClick={save}>
          Save Tag Config
        </LoadingButton>
      </div>
    </div>
  );
}
